import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { getProductDetailsDirect, updateStockItem } from '../../clients/product_client';
import { listStorageReviewLots } from '../../clients/storage_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import {
  buildStoragePlaceOptions,
  parseStoragePlace,
  stockItemFieldsForPlace,
} from '../../utils/storagePlace';
import StoragePlaceSelect from '../Product/StoragePlaceSelect';

const LOG_CONTEXT = 'StorageReviewQueue';

/**
 * Lots whose location text the import could not place. The list starts collapsed.
 * Opening it is what begins placing: each row picks any storage node and saves
 * through the existing stock-item update.
 *
 * @param {Object} props
 * @param {string} props.token
 * @param {Array} props.nodes
 * @param {Function} [props.onPlaced]
 * @param {number} [props.refreshSignal]
 */
const StorageReviewQueue = ({ token, nodes = [], refreshSignal = 0, onPlaced }) => {
  const [lots, setLots] = useState([]);
  const [places, setPlaces] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [open, setOpen] = useState(false);
  const storagePlaceOptions = useMemo(() => buildStoragePlaceOptions(nodes), [nodes]);

  const load = useCallback(() => {
    if (!token) {
      setLots([]);
      return;
    }
    logInfo(LOG_CONTEXT, 'load review lots');
    listStorageReviewLots(token).then(result => {
      if (!result.success) {
        if (!result.notDeployed) {
          logError(LOG_CONTEXT, 'review lots failed', {
            status: result.status ?? null,
          });
        }
        setLots([]);
        return;
      }
      setLots(result.data || []);
      logInfo(LOG_CONTEXT, 'review lots loaded', { lotCount: (result.data || []).length });
    });
  }, [token]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  if (!lots.length) return null;

  const save = async row => {
    const choice = places[row.stock_item_id] || {};
    const parsed = parseStoragePlace(nodes, choice.storageNodeId, choice.slotIndex);
    if (parsed.error || parsed.storage_node_id == null) {
      showToast(parsed.error || 'Choose a place', 'warning', 'top-right', 3500);
      return;
    }
    setSavingId(row.stock_item_id);
    logInfo(LOG_CONTEXT, 'place review lot', {
      stockItemId: row.stock_item_id,
      storageNodeId: parsed.storage_node_id,
      slotIndex: parsed.slot_index,
    });
    const product = await getProductDetailsDirect(token, row.product_id);
    const lot = (product?.items || []).find(
      item => String(item.id) === String(row.stock_item_id)
    );
    if (!lot) {
      setSavingId(null);
      logError(LOG_CONTEXT, 'review lot missing from product detail', {
        stockItemId: row.stock_item_id,
        productId: row.product_id,
      });
      showToast('Could not load that lot', 'warning', 'top-right', 4000);
      return;
    }
    const result = await updateStockItem(token, lot.id, {
      ...stockItemFieldsForPlace(lot),
      storageNodeId: parsed.storage_node_id,
      slotIndex: parsed.slot_index,
    });
    setSavingId(null);
    if (!result?.success) {
      const message = Array.isArray(result)
        ? String(result.flat()[0] ?? 'Could not place that lot')
        : 'Could not place that lot';
      logError(LOG_CONTEXT, 'review lot place rejected', {
        stockItemId: row.stock_item_id,
        message,
      });
      showToast(message, 'warning', 'top-right', 4000);
      return;
    }
    result.toast?.();
    logInfo(LOG_CONTEXT, 'review lot placed', { stockItemId: row.stock_item_id });
    onPlaced?.({ storageNodeId: parsed.storage_node_id });
    load();
  };

  const toggle = () => {
    setOpen(current => {
      const next = !current;
      logInfo(LOG_CONTEXT, next ? 'review list opened' : 'review list closed', {
        lotCount: lots.length,
      });
      return next;
    });
  };

  return (
    <section className="storage-review-queue" aria-labelledby="storage-review-title">
      <h2 id="storage-review-title" className="storage-review-queue__title">
        <button
          type="button"
          className="storage-review-queue__toggle"
          aria-expanded={open}
          onClick={toggle}
        >
          <span aria-hidden="true">{open ? '▾' : '▸'}</span>
          Needs a place
          <span className="storage-review-queue__count">{lots.length}</span>
        </button>
      </h2>
      {open ? (
        <>
      <p className="storage-review-queue__hint">
        These lots have a location note the import could not match. Choose any storage place.
      </p>
      <ul className="storage-review-queue__list">
        {lots.map(row => (
          <li key={row.stock_item_id} className="storage-review-queue__row">
            <div className="storage-review-queue__text">
              <span className="storage-review-queue__cat">{row.cat_num || 'CAT#'}</span>
              <span>{row.product_name}</span>
              <span>{row.batch ? `Lot ${row.batch}` : `Lot ${row.stock_item_id}`}</span>
              <span className="storage-review-queue__location">{row.location}</span>
              <span className="storage-review-queue__reason">{row.reason}</span>
            </div>
            <StoragePlaceSelect
              nodes={nodes}
              options={storagePlaceOptions}
              idPrefix={`review-${row.stock_item_id}`}
              storageNodeId={places[row.stock_item_id]?.storageNodeId ?? null}
              slotIndex={places[row.stock_item_id]?.slotIndex ?? null}
              onChange={value => {
                setPlaces(prev => ({ ...prev, [row.stock_item_id]: value }));
              }}
            />
            <Button
              size="sm"
              className="glass-primary-btn"
              disabled={savingId === row.stock_item_id}
              onClick={() => save(row)}
            >
              {savingId === row.stock_item_id ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Save place'
              )}
            </Button>
          </li>
        ))}
      </ul>
        </>
      ) : null}
    </section>
  );
};

export default StorageReviewQueue;
